<template>
    <div>
        <div class="flex justify-center">
            <transition name="fade" mode="out-in">
                <!-- Both pickers are a bordered list inside a centred card, so they share a width. -->
                <MainViewCard v-if="currentStep < 2" class="max-w-4xl px-4 py-4">
                    <transition name="fade" mode="out-in">
                        <component :is="currentComponent" :state="state" @next="next" @back="previous" />
                    </transition>
                </MainViewCard>
                <div v-else class="w-full">
                    <FarmToFarmPage v-if="selection" :selection="selection" @reset="onReset" />
                </div>
            </transition>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import MainViewCard from '@/components/common/MainViewCard.vue';
import { FarmToFarmSelection, FarmToFarmViewState } from '@/types';
import SelectFarms from './selectFarms/Index.vue';
import SelectPeriods from './selectPeriods/Index.vue';
import FarmToFarmPage from './farmToFarmPage/Index.vue';

function draftState(): FarmToFarmViewState {
    return {
        animalGroupId: null,
        selectedFarmIds: [],
        // Month keeps the behavior the report has always had, so an existing user's first visit
        // after the change looks exactly like their last one.
        periodType: 'Month',
        selectedPeriods: [],
        periodLabels: {},
    };
}

/**
 * Narrows the draft to the selection the report step needs. Returns null only if a Next button were
 * bypassed - both pickers disable theirs until their part of the draft is filled.
 */
function toSelection(state: FarmToFarmViewState): FarmToFarmSelection | null {
    if (state.animalGroupId === null || !state.selectedFarmIds.length || !state.selectedPeriods.length) return null;

    return {
        animalGroupId: state.animalGroupId,
        farmIds: state.selectedFarmIds,
        periodType: state.periodType,
        periods: state.selectedPeriods,
        periodLabels: state.periodLabels,
    };
}

/**
 * Two-step picker then the table, following the shape of ReportViewSteps.vue: a step index, a
 * component switch, and one shared state object mutated in place by each step.
 *
 * Unlike ReportViewSteps the report step does not get that draft. Its every field is required by
 * then, and a type that still admits a half-filled wizard forces it to guard a case it cannot
 * render. `next()` narrows once, on the way in, and is the single place that maintains the
 * invariant `currentStep === 2` implies `selection !== null`.
 */
export default defineComponent({
    components: { FarmToFarmPage, MainViewCard },
    data() {
        return {
            currentStep: 0,
            state: draftState(),
            selection: null as FarmToFarmSelection | null,
        };
    },
    computed: {
        currentComponent(): any {
            return this.currentStep === 1 ? SelectPeriods : SelectFarms;
        },
    },
    methods: {
        next() {
            if (this.currentStep === 1) {
                const selection = toSelection(this.state);
                // Unreachable while the period step's Next stays disabled on an empty selection.
                // Staying put beats advancing to a step that has nothing to render.
                if (!selection) return;

                this.selection = selection;
            }

            this.currentStep += 1;
        },
        /**
         * Deliberately does not touch `state`: the whole point of a Back button is that the draft
         * survives it. The farm picker's `groups` watcher already guards on the group still being set,
         * so the return trip lands on the selection the user left.
         */
        previous() {
            this.currentStep -= 1;
        },
        onReset() {
            this.currentStep = 0;
            this.state = draftState();
            this.selection = null;
        },
    },
});
</script>
